import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {EmojiDefinitionAndSkinTone} from '../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {inboxHasColor} from '../../../../helpers/modelHelpers';
import {getTagEmojiHighlight} from '../../../../helpers/models/highlightHelpers';
import {HighlightBaseModel} from '../../../../models/api/highlightBaseModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {LensModel} from '../../../../models/api/lensModel';
import {TagBehaviorsEnum, TagModel} from '../../../../models/api/tagModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {TeammatesFoldersEnum} from '../../../../models/url/inboxesUrlModel';
import {ResourceMap} from '../../../appTypes';
import {FindTeammate} from '../../../data/teammates/lazyTeammatesSelectors';
import {
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesTeamLensParameters,
  InboxesTeammateLensParameters,
  InboxesTeammateMessagesParameters,
  InboxesTeammateParameters,
  InboxesTeammateViewParameters,
  InboxesUserParameters,
  InboxesViewParameters,
} from './inboxesParameters';

export enum SearchLocalFilterTypesEnum {
  DOT = 'colorDot',
  EMOJI = 'emoji',
  ICON = 'icon',
  TEAM = 'team',
  TEAMMATE = 'teammate',
}

export interface SearchLocalFilterIcon {
  type: SearchLocalFilterTypesEnum.ICON;
  iconName: IconName;
  selectedIconName?: IconName;
}

interface SearchLocalFilterEmoji {
  type: SearchLocalFilterTypesEnum.EMOJI;
  emoji: EmojiDefinitionAndSkinTone;
}

interface SearchLocalFilterColorDot {
  type: SearchLocalFilterTypesEnum.DOT;
  color?: string;
}

interface SearchLocalFilterTeamIcon {
  type: SearchLocalFilterTypesEnum.TEAM;
  team: TeamModel;
}

interface SearchLocalFilterTeammateIcon {
  type: SearchLocalFilterTypesEnum.TEAMMATE;
  teammate: TeammateModel;
}

export type SearchLocalFilterIconType =
  | SearchLocalFilterIcon
  | SearchLocalFilterEmoji
  | SearchLocalFilterColorDot
  | SearchLocalFilterTeamIcon
  | SearchLocalFilterTeammateIcon;

export function getParametersSearchLocalFilterIcon(
  inboxes: ResourceMap<InboxModel>,
  tags: ResourceMap<TagModel>,
  lenses: ResourceMap<LensModel>,
  starredTagIds: ReadonlySet<number>,
  teams: ResourceMap<TeamModel>,
  findTeammate: FindTeammate,
  parameters: InboxesParameters,
): SearchLocalFilterIconType {
  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_INBOX: {
      const inbox = inboxes[parameters.inboxId];

      return {
        type: SearchLocalFilterTypesEnum.DOT,
        color: isDefined(inbox) && inboxHasColor(inbox) ? inbox.color : undefined,
      };
    }

    case InboxesParametersTypesEnum.USER:
    case InboxesParametersTypesEnum.TEAMMATE:
    case InboxesParametersTypesEnum.TEAMMATE_MESSAGES:
      return getTeammateParametersSearchLocalFilterIcon(findTeammate, parameters);

    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return getViewParametersSearchLocalFilterIcon(tags, starredTagIds, parameters);

    case InboxesParametersTypesEnum.TEAMMATE_LENS:
    case InboxesParametersTypesEnum.TEAM_LENS:
      return getLensParametersSearchLocalFilterIcon(lenses, parameters);

    case InboxesParametersTypesEnum.TEAM: {
      const team = teams[parameters.teamId];

      if (team) {
        return {
          type: SearchLocalFilterTypesEnum.TEAM,
          team,
        };
      }

      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'teammatesSmall',
        selectedIconName: 'teammatesFilledSmall',
      };
    }

    case InboxesParametersTypesEnum.GLOBAL:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'teammatesSmall',
        selectedIconName: 'teammatesFilledSmall',
      };

    default:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'inboxSmall',
        selectedIconName: 'inboxFilledSmall',
      };
  }
}

function getTeammateParametersSearchLocalFilterIcon(
  findTeammate: FindTeammate,
  parameters: InboxesUserParameters | InboxesTeammateParameters | InboxesTeammateMessagesParameters,
): SearchLocalFilterIconType {
  switch (parameters.folder) {
    case TeammatesFoldersEnum.ASSIGNED:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'assignSmall',
        selectedIconName: 'assignFilledSmall',
      };

    case TeammatesFoldersEnum.ASSIGNED_SHARED: {
      const teammate = findTeammate(parameters.teammateId, 'InboxesParametersSearchFiltersHelpers');

      if (teammate) {
        return {
          type: SearchLocalFilterTypesEnum.TEAMMATE,
          teammate,
        };
      }

      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'assignSmall',
        selectedIconName: 'assignFilledSmall',
      };
    }

    case TeammatesFoldersEnum.SHARED_WITH_ME:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'teammatesSmall',
        selectedIconName: 'teammatesFilledSmall',
      };

    case TeammatesFoldersEnum.DISCUSSIONS:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'discussionSmall',
        selectedIconName: 'discussionFilledSmall',
      };

    case TeammatesFoldersEnum.TRASHED:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'trashSmall',
        selectedIconName: 'trashFilledSmall',
      };

    case TeammatesFoldersEnum.SPAM:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'spamSmall',
        selectedIconName: 'spamFilledSmall',
      };

    case TeammatesFoldersEnum.SENT:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'sendSmall',
        selectedIconName: 'sendFilledSmall',
      };

    default:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'inboxSmall',
        selectedIconName: 'inboxFilledSmall',
      };
  }
}

function getViewParametersSearchLocalFilterIcon(
  tags: ResourceMap<TagModel>,
  starredTagIds: ReadonlySet<number>,
  parameters: InboxesViewParameters | InboxesTeammateViewParameters,
): SearchLocalFilterIconType {
  if (starredTagIds.has(parameters.viewId)) {
    return {
      type: SearchLocalFilterTypesEnum.ICON,
      iconName: 'starSmall',
      selectedIconName: 'starFilledSmall',
    };
  }

  const tag = tags[parameters.viewId];

  if (tag?.behaviors.includes(TagBehaviorsEnum.CAN_CUSTOM_VIEW)) {
    return {
      type: SearchLocalFilterTypesEnum.ICON,
      iconName: 'customViewsSmall',
      selectedIconName: 'customViewsFilledSmall',
    };
  }

  const emojiIcon = tag && getSearchLocalFilterEmojiHighlightIcon(tag);
  if (emojiIcon) {
    return emojiIcon;
  }

  return {
    type: SearchLocalFilterTypesEnum.ICON,
    iconName: 'tagSmall',
    selectedIconName: 'tagFilledSmall',
  };
}

function getLensParametersSearchLocalFilterIcon(
  lenses: ResourceMap<LensModel>,
  parameters: InboxesTeammateLensParameters | InboxesTeamLensParameters,
): SearchLocalFilterIconType {
  const lens = lenses[parameters.lensId];

  const emojiIcon = lens && getSearchLocalFilterEmojiHighlightIcon(lens);
  if (emojiIcon) {
    return emojiIcon;
  }

  // TODO-LENS: change to new lens icon.
  return {
    type: SearchLocalFilterTypesEnum.ICON,
    iconName: 'tagSmall',
    selectedIconName: 'tagFilledSmall',
  };
}

function getSearchLocalFilterEmojiHighlightIcon(
  highlightableModel: HighlightBaseModel,
): SearchLocalFilterEmoji | undefined {
  const emoji = getTagEmojiHighlight(highlightableModel);
  return emoji
    ? {
        type: SearchLocalFilterTypesEnum.EMOJI,
        emoji,
      }
    : undefined;
}
